/**
 * Dois modelos lado a lado: como cada um chega e a escada de numeros de cada
 * um, com o melhor de cada degrau marcado.
 *
 * Decisoes nao obvias:
 *  1. "Melhor" e decidido por TAXA com o denominador proprio de cada modelo
 *     (pagaram / receberam, responderam / chegaram), nunca por contagem
 *     absoluta: o modelo global sai para quatro empresas e sempre teria mais
 *     de tudo. Volume, "fechados pela vendedora" e valor recuperado aparecem
 *     sem vencedor, porque dependem do tamanho da base.
 *  2. Publicos, canais ou categorias diferentes ganham um aviso: a comparacao
 *     vira indicativa, nao um teste A/B.
 *  3. A acao principal e "Duplicar o melhor": o caminho natural depois de
 *     comparar e partir do que funciona para escrever a proxima variante.
 *  4. Modelo de publico que nao gera credito (mensagem da vendedora,
 *     pos-venda) mostra "sem crédito próprio" nas linhas de pagamento e nao
 *     disputa o "melhor": 0 pagamento ali nao e fracasso, e ele sempre
 *     "perderia" para um toque da regua.
 */

import { Badge, Button, Icon, Modal, Panel } from '../../../components/ui';
import { formatBRLFromCents, formatCount, formatPercentFromBps, formatRatio } from '../../../lib/format';
import type { CompanySummary } from '../../../types';
import { chaveDoGrupo, geraCredito, taxaBps, taxaPagamentoBps, type Ranking } from '../desempenho';
import type { MessageTemplate } from '../types';
import { CANAL_CURTO, MEDIDA_LABEL, STATUS_TOM, descreverPublico, statusLabel } from '../vocabulario';
import { PreviaDoCanal } from './PreviaDoCanal';

interface LinhaComparada {
  readonly rotulo: string;
  readonly a: string;
  readonly b: string;
  /** Numeros comparaveis (taxa em bps); `null` = sem vencedor nesta linha. */
  readonly ca: number | null;
  readonly cb: number | null;
}

const SEM_CREDITO = 'sem crédito próprio';

/** Taxa de pagamento so para quem gera credito; os outros ficam fora da disputa. */
function taxaComCredito(t: MessageTemplate): number | null {
  return geraCredito(t) ? taxaPagamentoBps(t.performance) : null;
}

function celula(parte: number | null, todo: number): string {
  return parte === null ? '—' : `${formatCount(parte)} · ${formatRatio(parte, todo)}`;
}

function linhas(a: MessageTemplate, b: MessageTemplate): readonly LinhaComparada[] {
  const pa = a.performance;
  const pb = b.performance;
  const rotulo = MEDIDA_LABEL[a.channel];
  return [
    { rotulo: 'Receberam', a: formatCount(pa.sent), b: formatCount(pb.sent), ca: null, cb: null },
    { rotulo: 'Chegaram', a: celula(pa.delivered, pa.sent), b: celula(pb.delivered, pb.sent), ca: taxaBps(pa.delivered, pa.sent), cb: taxaBps(pb.delivered, pb.sent) },
    {
      rotulo: a.channel === 'email' ? 'Abriram' : 'Leram',
      a: celula(pa.opened, pa.delivered),
      b: celula(pb.opened, pb.delivered),
      ca: pa.opened === null ? null : taxaBps(pa.opened, pa.delivered),
      cb: pb.opened === null ? null : taxaBps(pb.opened, pb.delivered),
    },
    {
      rotulo: 'Clicaram no link',
      a: celula(pa.clicked, pa.delivered),
      b: celula(pb.clicked, pb.delivered),
      ca: pa.clicked === null ? null : taxaBps(pa.clicked, pa.delivered),
      cb: pb.clicked === null ? null : taxaBps(pb.clicked, pb.delivered),
    },
    { rotulo: 'Responderam → vendedora', a: celula(pa.replied, pa.delivered), b: celula(pb.replied, pb.delivered), ca: taxaBps(pa.replied, pa.delivered), cb: taxaBps(pb.replied, pb.delivered) },
    {
      rotulo: rotulo.paid.charAt(0).toUpperCase() + rotulo.paid.slice(1),
      a: geraCredito(a) ? celula(pa.paid, pa.sent) : SEM_CREDITO,
      b: geraCredito(b) ? celula(pb.paid, pb.sent) : SEM_CREDITO,
      ca: taxaComCredito(a),
      cb: taxaComCredito(b),
    },
    { rotulo: 'Fechados pela vendedora', a: formatCount(pa.paidViaSeller), b: formatCount(pb.paidViaSeller), ca: null, cb: null },
    {
      rotulo: 'Recuperado (valor pago)',
      a: geraCredito(a) ? formatBRLFromCents(pa.recoveredCents) : SEM_CREDITO,
      b: geraCredito(b) ? formatBRLFromCents(pb.recoveredCents) : SEM_CREDITO,
      ca: null,
      cb: null,
    },
  ];
}

function Melhor({ ativo }: { readonly ativo: boolean }): JSX.Element | null {
  if (!ativo) {
    return null;
  }
  return (
    <Badge tone="good" className="ml-1.5">
      <span aria-hidden="true">▲</span> melhor
    </Badge>
  );
}

export function ModalCompararModelos({
  a,
  b,
  empresaDe,
  ranking,
  periodo,
  onClose,
  onDuplicar,
}: {
  readonly a: MessageTemplate;
  readonly b: MessageTemplate;
  readonly empresaDe: (t: MessageTemplate) => CompanySummary | null;
  readonly ranking: Ranking;
  readonly periodo: string;
  /** PRECISA ter identidade estavel (useCallback). */
  readonly onClose: () => void;
  readonly onDuplicar: (t: MessageTemplate) => void;
}): JSX.Element {
  const ta = taxaComCredito(a);
  const tb = taxaComCredito(b);
  const vencedor = ta === null || tb === null || ta === tb ? null : ta > tb ? a : b;
  const perdedor = vencedor === null ? null : vencedor.id === a.id ? b : a;
  const tv = vencedor === null ? null : taxaComCredito(vencedor);
  const tp = perdedor === null ? null : taxaComCredito(perdedor);
  const ganhoBps = tv !== null && tp !== null && tp > 0 ? Math.round(((tv - tp) * 10_000) / tp) : null;
  const mesmoGrupo = chaveDoGrupo(a) === chaveDoGrupo(b);
  const tabela = linhas(a, b);

  return (
    <Modal
      open
      onClose={onClose}
      size="xl"
      title="Comparar lado a lado"
      description={`${a.name} × ${b.name} · ${periodo}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Fechar
          </Button>
          {vencedor === null ? null : (
            <Button variant="primary" icon="copiar" onClick={() => onDuplicar(vencedor)}>
              Duplicar o melhor
            </Button>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-5">
        {vencedor !== null && perdedor !== null ? (
          <Panel tone="good" className="flex items-start gap-3 px-4 py-3">
            <Icon name="estrela" size="sm" className="mt-0.5 shrink-0 text-status-good" />
            <p className="text-sm text-ink-muted">
              <span className="font-semibold text-ink">
                {vencedor.name} converte {ganhoBps === null ? 'mais' : `${formatPercentFromBps(ganhoBps)} mais`}
              </span>{' '}
              que {perdedor.name}: {tv === null ? '—' : formatPercentFromBps(tv)} contra {tp === null ? '—' : formatPercentFromBps(tp)} de quem recebeu pagaram, com o crédito no próprio toque.
            </p>
          </Panel>
        ) : (
          <Panel className="flex items-start gap-3 px-4 py-3">
            <Icon name="info" size="sm" className="mt-0.5 shrink-0 text-ink-subtle" />
            <p className="text-sm text-ink-muted">Sem vencedor pela taxa de pagamento nesta janela: um dos dois ainda não tem envios, fala com um público que não gera crédito próprio ou as taxas empataram.</p>
          </Panel>
        )}
        {mesmoGrupo ? null : (
          <Panel tone="warning" className="flex items-start gap-3 px-4 py-3">
            <Icon name="alerta" size="sm" className="mt-0.5 shrink-0 text-status-warning" />
            <p className="text-sm text-ink-muted">
              Público, canal ou categoria diferentes ({descreverPublico(a)} no {CANAL_CURTO[a.channel]} × {descreverPublico(b)} no {CANAL_CURTO[b.channel]}): a comparação é indicativa, não
              um teste A/B.
            </p>
          </Panel>
        )}

        <div className="grid gap-5 md:grid-cols-2">
          {[a, b].map((t) => {
            const pos = ranking.get(t.id) ?? null;
            return (
              <section key={t.id} aria-label={`Modelo ${t.name}`} className="flex min-w-0 flex-col gap-3">
                <div className="min-w-0">
                  <p className="truncate font-mono text-sm font-semibold text-ink" title={t.name}>
                    {t.name}
                  </p>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    <Badge tone={STATUS_TOM[t.status]} dot>
                      {statusLabel(t.status, t.channel)}
                    </Badge>
                    <Badge tone="neutral">{descreverPublico(t)}</Badge>
                    {pos?.melhor === true ? (
                      <Badge tone="good" icon="estrela">
                        Melhor da categoria
                      </Badge>
                    ) : pos !== null ? (
                      <Badge tone="neutral">
                        {pos.posicao}º de {pos.de}
                      </Badge>
                    ) : null}
                  </div>
                </div>
                <PreviaDoCanal template={t} empresa={empresaDe(t)} compacto />
              </section>
            );
          })}
        </div>

        <div className="overflow-x-auto rounded-xl border border-line">
          <table className="w-full min-w-[32rem] text-sm">
            <caption className="sr-only">Números dos dois modelos em {periodo}</caption>
            <thead className="bg-elevated text-xs text-ink-subtle">
              <tr>
                <th scope="col" className="px-3 py-2 text-left font-medium">
                  Degrau
                </th>
                <th scope="col" className="px-3 py-2 text-right font-mono font-medium">
                  {a.name}
                </th>
                <th scope="col" className="px-3 py-2 text-right font-mono font-medium">
                  {b.name}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {tabela.map((l) => {
                const aMelhor = l.ca !== null && l.cb !== null && l.ca > l.cb;
                const bMelhor = l.ca !== null && l.cb !== null && l.cb > l.ca;
                return (
                  <tr key={l.rotulo}>
                    <th scope="row" className="px-3 py-2 text-left font-normal text-ink-muted">
                      {l.rotulo}
                    </th>
                    <td className="px-3 py-2 text-right tabular-nums text-ink">
                      {l.a}
                      <Melhor ativo={aMelhor} />
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-ink">
                      {l.b}
                      <Melhor ativo={bMelhor} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-ink-subtle">
          Cada taxa usa o denominador do próprio modelo (pagaram ÷ receberam; responderam ÷ chegaram). Volume, fechados pela vendedora e valor recuperado não têm vencedor: dependem do
          tamanho da base de cada um.
        </p>
      </div>
    </Modal>
  );
}
